import React, { useState } from 'react';
import { IoChevronBack, IoChevronForward, IoPlayCircle } from 'react-icons/io5';
import { useFetch } from '../../../hooks/useFetch';
import Loading from '../Loading';
import ErrorMessage from '../ErrorMessage';
import { getEmbedUrl, getVideoId } from '../../../utils/helpers'


const VideoCarousel = ({
    title = 'Video Galerimiz',
    itemsPerPage = 6,
    endpoint = 'posts',
    bgColor = '#1e3a8a',
    textColor = '#ffffff',
    cardBgColor = '#ffffff',
}) => {
    const [page, setPage] = useState(0);
    const [selectedVideoId, setSelectedVideoId] = useState(null);
    const [isMobile, setIsMobile] = useState(false);
    const [isHovering, setIsHovering] = useState(false);

    const { data, loading, error } = useFetch(endpoint, '?populate=*&sort[0]=publishedAt:desc');

    // Detect screen size
    React.useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth < 1024);
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Set initial selected video once data loads
    React.useEffect(() => {
        if (data && data.length > 0 && selectedVideoId === null) {
            setSelectedVideoId(data[0].id);
        }
    }, [data, selectedVideoId]);

    if (loading) return <Loading />;
    if (error) return <ErrorMessage message={error} />;

    const allVideos = data.filter(() => true);
    const mainVideo = allVideos.find(v => v.id === selectedVideoId) || allVideos[0];

    // Prepare items for the current page (keep original order)
    const currentItemsPerPage = isMobile ? 3 : itemsPerPage;
    const totalPages = Math.ceil(allVideos.length / currentItemsPerPage);
    const pagedVideos = allVideos.slice(page * currentItemsPerPage, (page + 1) * currentItemsPerPage);

    return (
        <div
            className="w-full px-4 py-8 md:px-6 md:py-10 lg:py-10  flex flex-col"
            style={{ background: bgColor, color: textColor }}
        >
            <div className="max-w-[1200px] mx-auto w-full">

                {/* Header */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 md:mb-8 gap-4">
                    <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold m-0">
                        {title}
                    </h1>

                    <div className="flex gap-2">
                        <button
                            onClick={() => setPage(p => Math.max(p - 1, 0))}
                            disabled={page === 0}
                            className={`w-10 h-10 md:w-12 md:h-12 rounded-full border-none transition-all duration-300 flex items-center justify-center
                                ${page === 0
                                    ? 'bg-white/20 cursor-not-allowed opacity-50'
                                    : 'bg-white/30 hover:bg-white/50 cursor-pointer hover:scale-110'
                                }`}
                            style={{ color: textColor }}
                        >
                            <IoChevronBack size={22} />
                        </button>

                        <button
                            onClick={() => setPage(p => Math.min(p + 1, totalPages - 1))}
                            disabled={page === totalPages - 1}
                            className={`w-10 h-10 md:w-12 md:h-12 rounded-full border-none transition-all duration-300 flex items-center justify-center
                                ${page === totalPages - 1
                                    ? 'bg-white/20 cursor-not-allowed opacity-50'
                                    : 'bg-white/30 hover:bg-white/50 cursor-pointer hover:scale-110'
                                }`}
                            style={{ color: textColor }}
                        >
                            <IoChevronForward size={22} />
                        </button>
                    </div>
                </div>

                {/* Layout: Stack on mobile, side-by-side on desktop */}
                <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-4 md:gap-6 w-full">

                    {/* MAIN VIDEO */}
                    <div className="flex flex-col gap-2">
                        <div
                            className="rounded-xl overflow-hidden h-[250px] sm:h-[350px] md:h-[450px] lg:h-[560px] flex justify-center items-center shadow-2xl relative"
                            style={{ background: cardBgColor }}
                            onMouseEnter={() => setIsHovering(true)}
                            onMouseLeave={() => setIsHovering(false)}
                        >
                            <iframe
                                className="w-full h-full"
                                src={getEmbedUrl(mainVideo?.video)}
                                title={mainVideo?.title || "Video"}
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                            />

                            {/* Video title overlay - only shows on hover and doesn't cover controls */}
                            <div
                                className={`absolute top-0 left-0 right-0 bg-gradient-to-b from-black/80 to-transparent p-4 transition-opacity duration-300 pointer-events-none
                                    ${isHovering ? 'opacity-100' : 'opacity-0'}`}
                            >
                                <p className="text-white font-semibold text-sm md:text-base line-clamp-2">
                                    {mainVideo?.title}
                                </p>
                            </div>
                        </div>

                        {/* Video title below the video (always visible) */}
                        <div className="px-2">
                            <p className="text-sm md:text-base font-semibold line-clamp-2" style={{ color: textColor }}>
                                {mainVideo?.title}
                            </p>
                        </div>
                    </div>

                    {/* VIDEO LIST */}
                    <div className="flex flex-col gap-3 md:gap-4">
                        {pagedVideos.map(video => {
                            const isSelected = video.id === selectedVideoId;
                            return (
                                <div
                                    key={video.id}
                                    onClick={() => setSelectedVideoId(video.id)}
                                    className={`flex gap-3 md:gap-4 cursor-pointer rounded-lg p-2 transition-all duration-300 items-center group
                                        ${isSelected ? 'bg-white/20 ring-2 ring-white/50' : 'hover:bg-white/10'}`}
                                >
                                    {/* Thumbnail */}
                                    <div className={`flex-shrink-0 w-28 h-20 sm:w-32 sm:h-24 md:w-40 md:h-28 rounded-lg overflow-hidden bg-black relative
                                        ${isSelected ? 'ring-2 ring-white' : ''}`}>
                                        <img
                                            src={`https://img.youtube.com/vi/${getVideoId(video.video)}/hqdefault.jpg`}
                                            alt={video.title}
                                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                        />

                                        {/* Play icon overlay */}
                                        <div className={`absolute inset-0 flex items-center justify-center transition-opacity duration-300
                                            ${isSelected ? 'bg-black/50 opacity-100' : 'bg-black/30 opacity-0 group-hover:opacity-100'}`}>
                                            <IoPlayCircle size={40} className="text-white drop-shadow-lg" />
                                        </div>
                                    </div>

                                    {/* Title */}
                                    <p
                                        className={`m-0 font-medium text-sm md:text-base flex-1 line-clamp-2 md:line-clamp-3 transition-all
                                            ${isSelected ? 'font-bold' : 'group-hover:text-opacity-90'}`}
                                        style={{ color: textColor }}
                                    >
                                        {video.title}
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Pagination */}
                <div className="mt-8 md:mt-12 flex justify-center gap-2">
                    {Array.from({ length: totalPages }).map((_, i) => (
                        <button
                            key={i}
                            onClick={() => setPage(i)}
                            className={`h-2 rounded-full border-none cursor-pointer transition-all duration-300 hover:opacity-80
                                ${i === page ? 'w-8 md:w-10' : 'w-2'}`}
                            style={{
                                background: i === page ? textColor : 'rgba(255,255,255,0.4)'
                            }}
                            aria-label={`Go to page ${i + 1}`}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
};

export default VideoCarousel;